import { fetchAuthSession, fetchUserAttributes } from "aws-amplify/auth"
import type { NavigateFunction } from "react-router-dom"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper"

import { GENERAL_CONFIG } from "../config/general.config"
import { ROUTES_CONFIG } from "../config/routes.config"
import { AuthService } from "../services/auth.service"
import type {
    IAuthDemoSession,
    IEagleEyeViewAsConfig,
} from "../types/objects.types.ts"
import { UtilHelper } from "./util.helper"

export class AuthHelper {
    /**
     * Check if user is logged in
     */
    static async isLoggedIn(): Promise<boolean> {
        return !!(await AuthHelper.getAccessToken())
    }

    /**
     * Whether a valid demo session is stored locally.
     */
    static isDemoSessionActive(): boolean {
        return !!AuthHelper.getDemoSession()
    }

    /**
     * Read demo token from local storage.
     */
    static getDemoSession(): IAuthDemoSession | null {
        const session = BrowserStorageHelper.get<IAuthDemoSession | null>(
            GENERAL_CONFIG.browserStorageKeys.demoSession,
            null,
            BrowserStorageType.localStorage,
        )

        if (!session?.accessToken || !session.isDemo) {
            return null
        }

        return session
    }

    /**
     * Clear demo session from local storage.
     */
    static clearDemoSession(): void {
        BrowserStorageHelper.remove(
            GENERAL_CONFIG.browserStorageKeys.demoSession,
            BrowserStorageType.localStorage,
        )
    }

    /**
     * Set demo session in local storage.
     */
    static setDemoSession(session: IAuthDemoSession): void {
        BrowserStorageHelper.set(
            GENERAL_CONFIG.browserStorageKeys.demoSession,
            session,
            BrowserStorageType.localStorage,
        )
    }

    /**
     * Get access token from demo session or Amplify session.
     */
    static async getAccessToken(): Promise<string | undefined> {
        try {
            const demoSession = AuthHelper.getDemoSession()

            if (demoSession?.accessToken) {
                return demoSession.accessToken
            }

            const session = await fetchAuthSession()

            // Explicitly use access token for all user types, including federated users
            return session.tokens?.accessToken?.toString()
        } catch (e) {
            return undefined
        }
    }

    /**
     * Process user after successful login
     */
    static async processUserSuccessLoggedIn(
        navigate?: NavigateFunction,
    ): Promise<boolean> {
        try {
            const jwtToken = await AuthHelper.getAccessToken()

            if (!jwtToken) {
                ToastrHelper.error("Could not authenticate user")

                return false
            }

            BrowserStorageHelper.set(
                GENERAL_CONFIG.browserStorageKeys.needTrackLoggedInEvent,
                true,
                BrowserStorageType.sessionStorage,
            )

            // Handle redirectTo from url query
            UtilHelper.redirectTo(
                UtilHelper.getRawUrlSearchParam(
                    GENERAL_CONFIG.urlSearchParamsKeys.redirectTo,
                ) || ROUTES_CONFIG.baseApplicationUrl,
                navigate,
            )

            setTimeout(() => {
                UtilHelper.checkAppVersionUpdateNeeded()
            })

            return true
        } catch (e) {
            return false
        }
    }

    /**
     * Set view as data for Eagle Eye functionality
     * that allows to view as any other user and view any practice that that user has access to
     */
    static setEagleEyeViewAsData(
        data: IEagleEyeViewAsConfig,
        withReload = true,
    ) {
        BrowserStorageHelper.set(
            GENERAL_CONFIG.browserStorageKeys.adminViewAsKey,
            data,
            BrowserStorageType.localStorage,
        )

        if (withReload) {
            // Set the impersonated practice id in local storage;
            // mimics behavior of userDetailsSetSelectedPracticeRole
            BrowserStorageHelper.set(
                GENERAL_CONFIG.browserStorageKeys.selectedPracticeId,
                data.practice.id,
                BrowserStorageType.localStorage,
            )

            window.location.assign("/")
        }
    }

    /**
     * Get view as data for Eagle Eye functionality
     */
    static getEagleEyeViewAsData(): IEagleEyeViewAsConfig | null {
        return BrowserStorageHelper.get(
            GENERAL_CONFIG.browserStorageKeys.adminViewAsKey,
            null,
            BrowserStorageType.localStorage,
        )
    }

    /**
     * Delete view as data for Eagle Eye functionality
     */
    static removeEagleEyeViewAsData(withReload = true) {
        // Get the eagle eye data before removing below
        const eagleEyeData = AuthHelper.getEagleEyeViewAsData()

        if (!eagleEyeData) {
            return
        }

        BrowserStorageHelper.remove(
            GENERAL_CONFIG.browserStorageKeys.adminViewAsKey,
            BrowserStorageType.localStorage,
        )

        if (withReload) {
            // Set the return to practice id in local storage;
            // mimics behavior of userDetailsSetSelectedPracticeRole
            BrowserStorageHelper.set(
                GENERAL_CONFIG.browserStorageKeys.selectedPracticeId,
                eagleEyeData.eagleEyeReturnToPracticeId,
                BrowserStorageType.localStorage,
            )

            window.location.assign("/")
        }
    }

    /**
     * Get phone number that is used for MFA
     */
    static async getMFAPhoneNumber(ignoreCache = true): Promise<string | null> {
        try {
            const attributes = ignoreCache
                ? await fetchUserAttributes()
                : (await AuthService.getAuthCache()).fetchedUserAttributes

            return attributes.phone_number_verified === "true"
                ? attributes.phone_number || null
                : null
        } catch (e) {
            return null
        }
    }
}
